import { Link } from "react-router-dom";
import { MarketingHeader } from "@/components/marketing/MarketingHeader";
import { HardHat, Check, ClipboardList, ShieldAlert, Truck, Banknote, Calendar, Users, MapPin } from "lucide-react";
import { SEO } from "@/lib/seo";
import { PublicFooter } from "./FAQ";
import { RelatedPages } from "@/components/RelatedPages";
import { Link as RLink } from "react-router-dom";

const orgJsonLd = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Fieldlink",
  url: "https://www.fieldlink.co.nz",
  logo: "https://www.fieldlink.co.nz/icon-512.png",
  description: "Civil construction management software built in New Zealand for NZ civil contractors.",
  areaServed: "NZ",
};

const softwareJsonLd = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "Fieldlink",
  applicationCategory: "BusinessApplication",
  operatingSystem: "Web, Android, iOS (PWA)",
  url: "https://www.fieldlink.co.nz",
  description: "Civil construction software for NZ contractors — takeoffs, estimating, quotes, jobs, crews, plant, timesheets, safety, QA, job costing and invoicing in one platform.",
};

const PLATFORM_CLUSTER = [
  { to: "/civil-estimating-software-nz", label: "Civil Estimating" },
  { to: "/construction-takeoff-software-nz", label: "Takeoffs" },
  { to: "/ai-construction-plan-reader", label: "AI Plan Reader" },
  { to: "/drainage-estimating-software", label: "Drainage Estimating" },
  { to: "/earthworks-estimating-software", label: "Earthworks Estimating" },
  { to: "/job-management-nz", label: "Job Management" },
  { to: "/civil-job-costing-software-nz", label: "Job Costing" },
  { to: "/civil-crew-scheduling-software-nz", label: "Crew Scheduling" },
  { to: "/construction-qa-software-nz", label: "Safety & QA" },
  { to: "/fleet-management-nz", label: "Plant & Fleet" },
  { to: "/timesheet-app-nz", label: "Timesheets" },
  { to: "/resources", label: "Free Calculators" },
];

// Keyword targets: "civil construction software NZ",
// "construction management software New Zealand", "civil contractor app NZ",
// "earthworks software NZ", "roading contractor software NZ"
const CIVIL_FAQS = [
  { q: "What software do NZ civil construction companies use?", a: "Most NZ civil crews run a mix of spreadsheets, a whiteboard and one big overseas platform like Simpro. Purpose-built NZ options like Fieldlink combine job management, timesheets, SSSPs, fleet, truck dispatch and payroll in one flat-rate app, replacing the spreadsheet pile without the per-user pricing." },
  { q: "Does Fieldlink handle earthworks and roading crews?", a: "Yes. Fieldlink tracks plant run-hours per machine per job, pre-start inspections, cartage and truck dispatch with loads and tonnes, fuel, and cost-per-job back-costing — the day-to-day of an earthworks, drainage or roading contractor." },
  { q: "Can the office see what every job has cost so far?", a: "Yes — every job builds a live cost breakdown from labour hours at real rates, plant at internal charge-out rates, materials taken from the yard and confirmed supplier invoices, shown against the quoted value with a live margin percentage." },
  { q: "Is Fieldlink actually made in New Zealand?", a: "Yes — Fieldlink is 100% NZ-owned and built in Auckland for NZ civil work: SSSPs, Holidays Act pay rules, NZD flat pricing and NZ support hours." },
];
const civilFaqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: CIVIL_FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};
export default function CivilConstructionSoftwareNZ() {
  return (
    <div className="web-seo bg-[#f6f5f1] text-[#142635] min-h-screen">
      <SEO
        title="Civil Construction Software NZ — Built for Kiwi Contractors | Fieldlink"
        description="The NZ-made software for civil construction. Jobs, timesheets, SSSPs, fleet, payroll, dispatch — one app for the crew and the office. Built in Aotearoa for NZ contractors. 7-day free trial."
        path="/civil-construction-software-nz"
        jsonLd={[civilFaqJsonLd, orgJsonLd, softwareJsonLd]}
      />

      <Header />

      <section className="bg-[#f6f5f1] text-[#142635] border-b border-[#cbd3d5] py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="block text-[11px] font-mono font-bold uppercase tracking-[0.28em] text-[#a63c0b] mb-4">100% NZ-owned · Built in Aotearoa</div>
          <h1 className="font-display font-black uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-none mb-5">
            Civil Construction Software<br /><span className="text-[#a63c0b]">For NZ Contractors</span>
          </h1>
          <p className="text-[#465562] text-base sm:text-lg max-w-2xl">
            Fieldlink replaces the spreadsheet pile of timesheets, SSSPs, plant logs, dispatch boards and pay-run mess. One app for the crew on site, one app for the office — built in NZ for NZ civil work.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/register" className="rounded-md bg-[#FF5F15] hover:bg-[#E65100] text-[#142635] font-bold uppercase tracking-wider px-6 py-3" data-testid="civil-cta">Start free trial</Link>
            <Link to="/pricing" className="rounded-md border border-[#cbd3d5] bg-white hover:bg-white text-[#142635] font-bold uppercase tracking-wider px-6 py-3">See pricing</Link>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 md:px-12 py-16">
        <h2 className="font-display font-black uppercase tracking-tight text-3xl sm:text-4xl mb-3">Who Fieldlink is for</h2>
        <p className="text-[#52616c] max-w-2xl mb-10">
          Roading, drainage, earthworks, groundworks, demolition, traffic management and any civil contractor in New Zealand from 3 to 300 crew.
        </p>
        <div className="grid md:grid-cols-3 gap-6">
          <Card icon={ClipboardList} title="Roading + earthworks" body="Daily plant hours, kilometre logs, RUC tracking via Cartrack, project-level cost roll-up." />
          <Card icon={ShieldAlert} title="Drainage + utilities" body="Hot work permits, confined space SSSPs, locator surveys recorded in the field on PWA." />
          <Card icon={Truck} title="Tier 2/3 subcontractors" body="Sub-out reports for the main contractor — SSSPs, timesheets, daily diaries auto-generated." />
        </div>
      </section>

      <section className="bg-[#e9eeec] border-y border-[#cbd3d5] py-16">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <h2 className="font-display font-black uppercase tracking-tight text-3xl sm:text-4xl mb-8">Everything in the box</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {[
              { i: ClipboardList, t: "Timesheets", b: "Holidays Act 2003 rules built in, PWA on any phone, GPS clock-in." },
              { i: ShieldAlert, t: "SSSP generator", b: "AI-draft SSSPs in 90s. NZTA / council / Tier 1 ready." },
              { i: Truck, t: "Fleet + RUC", b: "Live tracking, pre-starts, damage reports, RUC monitoring." },
              { i: Banknote, t: "Pay runs", b: "Weekly pay, public holidays, Xero / iPayroll export." },
              { i: Calendar, t: "Dispatch", b: "Drag-and-drop crew + plant + jobs. Multi-job days supported." },
              { i: Users, t: "Toolbox + pre-starts", b: "Daily safety talks signed on-screen. Audit trail for ConstructSafe." },
              { i: MapPin, t: "Quotes + variations", b: "Rate libraries, change orders, customer invoices." },
              { i: Check, t: "Inbox + chat", b: "One Messenger-style org chat. Replaces the WhatsApp group." },
              { i: Check, t: "Forms + diaries", b: "Daily site diaries, hazard registers, near-miss reports." },
            ].map(({ i: I, t, b }) => (
              <div key={t} className="rounded-lg bg-white border border-[#cbd3d5] p-4">
                <I className="w-5 h-5 text-[#a63c0b] mb-2" />
                <div className="font-bold uppercase tracking-wider text-sm">{t}</div>
                <div className="text-xs text-[#52616c] mt-1">{b}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 md:px-12 py-16">
        <h2 className="font-display font-black uppercase tracking-tight text-3xl sm:text-4xl mb-3">NZ compliance, baked in</h2>
        <p className="text-[#52616c] max-w-2xl mb-10">
          We obsess over the NZ-specific stuff so you don't have to. None of this is a paid add-on — it's just how Fieldlink works.
        </p>
        <div className="grid md:grid-cols-2 gap-6">
          <Card icon={Calendar} title="Holidays Act 2003" body="Public holidays auto-applied per region (Auckland Anniversary, Otago Anniversary, etc). 1.5x on Sundays where applicable. Sick / annual leave / alternative days tracked separately." />
          <Card icon={ShieldAlert} title="Connected safety records" body="Keep job SSSPs, forms and safety reports available to the field and office, with assigned follow-up on safety reports." />
          <Card icon={Truck} title="NZTA, council and Tier 1 ready" body="SSSPs accepted on Fulton Hogan, Downer, Higgins, HEB, McConnell Dowell, BCC, Fletcher, NZTA + Auckland Transport sites." />
          <Card icon={Banknote} title="Xero, iPayroll, MYOB" body="Pay runs export straight to Xero (Payroll), iPayroll CSV, MYOB / Smartly. No double-typing." />
        </div>
      </section>

      <section className="bg-[#e9eeec] border-y border-[#cbd3d5] text-[#142635] py-16">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-display font-black uppercase tracking-tight text-3xl sm:text-4xl mb-4">See it on your own jobs in 5 minutes</h2>
          <p className="text-[#52616c] mb-8">7-day free trial. No credit card. NZ-based support.</p>
          <Link to="/register" className="inline-block rounded-md bg-[#FF5F15] hover:bg-[#E65100] text-[#142635] font-bold uppercase tracking-wider px-8 py-4">Start free trial</Link>
          <div className="mt-6 text-xs text-[#52616c]">
            <Link to="/sssp-generator-nz" className="underline hover:text-[#142635]">SSSP generator</Link> ·{" "}
            <Link to="/fleet-management-nz" className="underline hover:text-[#142635]">Fleet management</Link> ·{" "}
            <Link to="/timesheet-app-nz" className="underline hover:text-[#142635]">Timesheet app</Link> ·{" "}
            <Link to="/simpro-alternative-nz" className="underline hover:text-[#142635]">Simpro alternative</Link>
          </div>
        </div>
      </section>

      {/* Platform cluster — internal links to every major product page */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 py-14">
        <h2 className="font-display font-black uppercase tracking-tight text-2xl mb-2">One connected civil platform</h2>
        <p className="text-[#465562] mb-6 max-w-3xl">Takeoff → estimate → quote → job → crew & plant → field → safety & QA → job costing → invoice → paid. Every part of Fieldlink links to the next, so nothing gets re-typed.</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="platform-cluster">
          {PLATFORM_CLUSTER.map((p) => (
            <RLink key={p.to} to={p.to} className="rounded-md border border-[#cbd3d5] bg-white hover:border-[#FF5F15]/60 px-4 py-3 text-sm font-bold uppercase tracking-wide text-[#344451] transition-colors">{p.label}</RLink>
          ))}
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-6 md:px-12 py-10"><p className="text-[#465562]">For the day-to-day connection between crews, site records and costs, see <Link to="/job-management-nz" className="underline underline-offset-4">how Fieldlink manages a job from office to field</Link>. Keep the crew’s site records connected to the <Link to="/safety-management-nz" className="underline underline-offset-4">civil health and safety workspace</Link>.</p></section>

      {/* FAQ — matches the FAQPage schema injected in <SEO jsonLd> */}
      <section className="max-w-3xl mx-auto px-6 md:px-12 py-16">
        <h2 className="font-display font-black uppercase tracking-tight text-3xl mb-8">Civil construction software FAQs</h2>
        <div className="space-y-6">
          {CIVIL_FAQS.map((f) => (
            <div key={f.q} className="rounded-lg border border-[#cbd3d5] bg-white p-6">
              <h3 className="font-display font-bold uppercase tracking-tight text-lg mb-2">{f.q}</h3>
              <p className="text-sm text-[#465562] leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <RelatedPages current="/civil-construction-software-nz" />

      <PublicFooter />
    </div>
  );
}

function Header() {
  return (
      <MarketingHeader />
  );
}

function Card({ icon: Icon, title, body }) {
  return (
    <div className="rounded-lg border border-[#cbd3d5] bg-white p-6">
      <Icon className="w-7 h-7 text-[#a63c0b] mb-4" />
      <h3 className="font-display font-extrabold uppercase tracking-tight text-xl mb-2">{title}</h3>
      <p className="text-[#52616c] text-sm leading-relaxed">{body}</p>
    </div>
  );
}
